import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

export default function CompliancePage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="bg-tea-700 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Legal & Compliance</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
            Research Use Disclosure
          </h1>
          <p className="mt-4 text-tea-200 text-lg max-w-xl leading-relaxed">
            Aloha Amino products are sold exclusively for in-vitro and laboratory research. Here is
            exactly what that means, who may purchase, and what the rules are.
          </p>
          <div className="mt-8">
            <Link
              to="/coa"
              className="inline-flex items-center gap-2 bg-white text-tea-800 font-semibold px-6 py-3 rounded-lg shadow-sm hover:bg-tea-50 transition-colors"
            >
              <span aria-hidden="true">🧪</span>
              See COA
            </Link>
          </div>
        </div>
      </section>

      {/* Key notice */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex gap-3">
            <span className="text-2xl shrink-0" aria-hidden="true">⚠️</span>
            <p className="text-amber-900 text-sm leading-relaxed">
              <strong>FOR LABORATORY RESEARCH USE ONLY.</strong> Aloha Amino products are not drugs, foods,
              cosmetics, or medical devices. They have not been approved by applicable health authorities, the U.S. FDA,
              or any other regulatory body for human or veterinary use, treatment, diagnosis, or
              prevention of any disease or condition. By purchasing, you confirm you are a qualified
              researcher or laboratory provider and will use products only for legitimate in-vitro or
              ex-vivo research purposes.
            </p>
          </div>
        </div>
      </div>

      <main className="flex-1 py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 mb-16">
            {/* Who may purchase */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-tea-100 text-tea-700 text-sm font-bold">✓</span>
                Who May Purchase
              </h2>
              <ul className="space-y-3 text-gray-700 text-[15px]">
                {[
                  'Licensed researchers at accredited research institutions',
                  'Postdoctoral fellows with active lab affiliation',
                  'Graduate students under institutional supervision',
                  'Qualified laboratory providers and CROs',
                  'Medical and scientific professionals purchasing for bona-fide research',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-tea-600 mt-0.5 shrink-0">●</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Who may NOT purchase */}
            <div className="bg-red-50 rounded-2xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600 text-sm font-bold">✗</span>
                Who May NOT Purchase
              </h2>
              <ul className="space-y-3 text-gray-700 text-[15px]">
                {[
                  'Members of the general public for personal use',
                  'Anyone intending to use products on humans or animals',
                  'Resellers targeting consumer markets',
                  'Anyone making health, therapeutic, or medical claims about these products',
                  'Minors (under 21 years of age)',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-red-400 mt-0.5 shrink-0">●</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sections */}
          <div className="flex flex-col gap-12">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What "In-Vitro Research" Means</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                "In-vitro" means experiments conducted in a controlled environment outside of a living organism —
                in test tubes, cell cultures, Petri dishes, or laboratory equipment. "Ex-vivo" refers to experiments
                on tissue removed from a living organism for the duration of the experiment.
              </p>
              <p className="text-gray-600 leading-relaxed">
                In neither case should Aloha Amino products be introduced into a living human or animal. Use of our
                products for any in-vivo purpose is strictly prohibited and may violate applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Regulatory Context</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Peptides sold for research purposes are subject to applicable drug and food safety laws
                when used as drugs. Purchasing these products for human use without the appropriate drug
                authorisation may constitute a violation of those laws.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Aloha Amino operates in compliance with applicable research-chemical guidelines and does not
                ship internationally. All purchasers are verified for research eligibility before orders are
                fulfilled.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility Verification</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                All Aloha Amino accounts are reviewed for research eligibility before orders are fulfilled. We may
                ask for institutional email confirmation, proof of affiliation, or a brief description of your
                research program.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Misrepresentation of your credentials is a breach of our Terms of Service and may constitute
                fraud under applicable law. Aloha Amino reserves the right to report suspected
                misrepresentation to the appropriate authorities.
              </p>
            </section>

            <section className="bg-tea-50 rounded-2xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Questions?</h2>
              <p className="text-gray-600 mb-5 text-[15px] leading-relaxed">
                If you have questions about whether you are eligible to purchase or what our products may be
                used for, contact our compliance team.
              </p>
              <a href="mailto:support@alohaamino.example.com?subject=Compliance%20Question" className="btn-primary">
                Contact Compliance Team
              </a>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <Link to="/terms" className="text-tea-700 font-medium hover:underline">Terms of Service</Link>
                <Link to="/privacy" className="text-tea-700 font-medium hover:underline">Privacy Policy</Link>
                <Link to="/support" className="text-tea-700 font-medium hover:underline">Lab Support FAQ</Link>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
